import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, TrendingUp, TrendingDown, DollarSign, FileText, Download, Upload, Trash2, Building2, Check, Receipt, Clock, BarChart3 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { value: 'materiali', label: 'Materiali' },
  { value: 'manodopera', label: 'Manodopera' },
  { value: 'costo_personale', label: 'Costo Personale' },
  { value: 'noleggi', label: 'Noleggi' },
  { value: 'trasporti', label: 'Trasporti' },
  { value: 'subappalti', label: 'Subappalti' },
  { value: 'servizi', label: 'Servizi' },
  { value: 'bonifico', label: 'Bonifico Bancario' },
  { value: 'altro', label: 'Altro' },
];

export default function SiteAccountingPage() {
  const { isRole } = useAuth();
  const [sites, setSites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('__all__');
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [form, setForm] = useState({
    site_id: '',
    type: 'uscita',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    invoice_number: '',
    invoice_date: '',
    counterpart: '',
    description: '',
    category: 'materiali',
    payment_status: 'da_pagare',
    payment_method: '',
    bank_iban: '',
    bank_cro: '',
    bank_valuta: '',
    notes: ''
  });

  const currentYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState(currentYear);

  const load = async () => {
    try {
      const [sitesRes, reportRes, dashRes] = await Promise.all([
        api.get('/sites'),
        api.get('/site-accounting/report', { params: { year: filterYear } }),
        api.get('/site-accounting/dashboard', { params: { year: filterYear } }).catch(() => ({ data: null })),
      ]);
      setSites(sitesRes.data);
      setReport(reportRes.data);
      setDashboard(dashRes.data);
      
      // Load entries for selected site
      if (selectedSite && selectedSite !== '__all__') {
        const entriesRes = await api.get(`/site-accounting/by-site/${selectedSite}`);
        setEntries(entriesRes.data.entries || []);
      } else {
        const allRes = await api.get('/site-accounting', { params: { year: filterYear } });
        setEntries(allRes.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedSite, filterYear]);

  const handleCreate = async () => {
    try {
      await api.post('/site-accounting', {
        ...form,
        amount: parseFloat(form.amount) || 0
      });
      setShowCreate(false);
      setForm({
        site_id: '',
        type: 'uscita',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        invoice_number: '',
        invoice_date: '',
        counterpart: '',
        description: '',
        category: 'materiali',
        payment_status: 'da_pagare',
        notes: ''
      });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Eliminare questo movimento?')) return;
    try {
      await api.delete(`/site-accounting/${entryId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUpload = async (entryId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/site-accounting/${entryId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Calculate totals for selected view
  const totals = entries.reduce((acc, e) => {
    if (e.type === 'entrata') acc.entrate += e.amount || 0;
    else acc.uscite += e.amount || 0;
    return acc;
  }, { entrate: 0, uscite: 0 });
  totals.utile = totals.entrate - totals.uscite;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento contabilita...</div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-accounting-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Contabilita Cantieri</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(parseInt(v))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[currentYear, currentYear - 1, currentYear - 2].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-48" data-testid="filter-site">
              <Building2 size={14} className="mr-1" />
              <SelectValue placeholder="Seleziona cantiere" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti i cantieri</SelectItem>
              {sites.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isRole('admin', 'qhse') && (
            <Button onClick={() => setShowCreate(true)} data-testid="add-entry-btn">
              <Plus size={16} className="mr-1" /> Nuovo Movimento
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard Finanziaria SAL + Contabilita */}
      {dashboard && (
        <div className="space-y-4">
          {/* Row 1: SAL Totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="border-slate-200 bg-slate-50/50">
              <CardContent className="p-3 text-center">
                <Clock size={16} className="mx-auto text-slate-500 mb-1" />
                <p className="text-xs text-slate-500">SAL Inserito</p>
                <p className="text-lg font-bold text-slate-700" data-testid="sal-inserito">{formatCurrency(dashboard.sal_totals?.inserito || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 text-center">
                <Check size={16} className="mx-auto text-green-500 mb-1" />
                <p className="text-xs text-green-600">SAL Approvato</p>
                <p className="text-lg font-bold text-green-700" data-testid="sal-approvato">{formatCurrency(dashboard.sal_totals?.approvato || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 text-center">
                <Receipt size={16} className="mx-auto text-blue-500 mb-1" />
                <p className="text-xs text-blue-600">SAL Fatturato</p>
                <p className="text-lg font-bold text-blue-700" data-testid="sal-fatturato">{formatCurrency(dashboard.sal_totals?.fatturato || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-3 text-center">
                <DollarSign size={16} className="mx-auto text-purple-500 mb-1" />
                <p className="text-xs text-purple-600">SAL Incassato</p>
                <p className="text-lg font-bold text-purple-700" data-testid="sal-incassato">{formatCurrency(dashboard.sal_totals?.incassato || 0)}</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-300 bg-emerald-50/50">
              <CardContent className="p-3 text-center">
                <BarChart3 size={16} className="mx-auto text-emerald-500 mb-1" />
                <p className="text-xs text-emerald-600">SAL Totale</p>
                <p className="text-lg font-bold text-emerald-700">{formatCurrency(dashboard.sal_totals?.totale || 0)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Utile */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 flex items-center gap-3">
                <TrendingUp size={20} className="text-green-600 shrink-0" />
                <div>
                  <p className="text-lg font-bold text-green-700">{formatCurrency(totals.entrate + (dashboard.sal_totals?.incassato || 0))}</p>
                  <p className="text-xs text-green-600">Entrate Totali</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-3 flex items-center gap-3">
                <TrendingDown size={20} className="text-red-600 shrink-0" />
                <div>
                  <p className="text-lg font-bold text-red-700">{formatCurrency(dashboard.uscite_manuali || totals.uscite)}</p>
                  <p className="text-xs text-red-600">Uscite</p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border-2 ${(dashboard.utile_presunto || 0) >= 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <DollarSign size={20} className={`${(dashboard.utile_presunto || 0) >= 0 ? 'text-green-600' : 'text-red-600'} shrink-0`} />
                <div>
                  <p className={`text-lg font-bold ${(dashboard.utile_presunto || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="utile-presunto">{formatCurrency(dashboard.utile_presunto || 0)}</p>
                  <p className="text-xs text-slate-600">Utile Presunto</p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border-2 ${(dashboard.utile_reale || 0) >= 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <DollarSign size={20} className={`${(dashboard.utile_reale || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} shrink-0`} />
                <div>
                  <p className={`text-lg font-bold ${(dashboard.utile_reale || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="utile-reale">{formatCurrency(dashboard.utile_reale || 0)}</p>
                  <p className="text-xs text-slate-600">Utile Reale</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard Cantieri</TabsTrigger>
          <TabsTrigger value="movimenti">Movimenti ({entries.length})</TabsTrigger>
          <TabsTrigger value="flusso">Flusso Mensile</TabsTrigger>
        </TabsList>

        {/* Dashboard Cantieri */}
        <TabsContent value="dashboard">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cantiere</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-right">SAL Totale</TableHead>
                    <TableHead className="text-right hidden md:table-cell">Approvato</TableHead>
                    <TableHead className="text-right hidden md:table-cell">Fatturato</TableHead>
                    <TableHead className="text-right hidden md:table-cell">Incassato</TableHead>
                    <TableHead className="text-right">Uscite</TableHead>
                    <TableHead className="text-right">Utile Presunto</TableHead>
                    <TableHead className="text-right">Utile Reale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!dashboard?.sites?.length ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Nessun dato finanziario</TableCell></TableRow>
                  ) : dashboard.sites.map((s, i) => (
                    <TableRow key={i} className="hover:bg-slate-50">
                      <TableCell className="font-medium text-sm">{s.site_name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{s.client || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-bold">{formatCurrency(s.sal_totale)}</TableCell>
                      <TableCell className="text-right text-sm text-green-700 hidden md:table-cell">{formatCurrency(s.sal_approvato)}</TableCell>
                      <TableCell className="text-right text-sm text-blue-700 hidden md:table-cell">{formatCurrency(s.sal_fatturato)}</TableCell>
                      <TableCell className="text-right text-sm text-purple-700 hidden md:table-cell">{formatCurrency(s.sal_incassato)}</TableCell>
                      <TableCell className="text-right text-sm text-red-600">{formatCurrency(s.uscite)}</TableCell>
                      <TableCell className="text-right text-sm">
                        <span className={s.utile_presunto >= 0 ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{formatCurrency(s.utile_presunto)}</span>
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        <span className={s.utile_reale >= 0 ? 'text-emerald-700 font-bold' : 'text-red-700 font-bold'}>{formatCurrency(s.utile_reale)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movimenti">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Cantiere</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Fattura</TableHead>
                    <TableHead>Controparte</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="hidden md:table-cell">File</TableHead>
                    {isRole('admin') && <TableHead className="w-16">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        Nessun movimento trovato
                      </TableCell>
                    </TableRow>
                  ) : entries.map(entry => (
                    <TableRow key={entry.id} className={`hover:bg-slate-50 ${entry.type === 'entrata' ? 'bg-green-50/30' : ''}`} data-testid={`entry-row-${entry.id}`}>
                      <TableCell>
                        {entry.type === 'entrata' ? (
                          <TrendingUp size={16} className="text-green-600" />
                        ) : (
                          <TrendingDown size={16} className="text-red-600" />
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{entry.date}</TableCell>
                      <TableCell className="text-sm font-medium">{entry.site_name || 'N/A'}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{entry.description || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{entry.invoice_number || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{entry.counterpart || '-'}</TableCell>
                      <TableCell className={`text-sm font-medium text-right ${entry.type === 'entrata' ? 'text-green-700' : 'text-red-700'}`}>
                        {entry.type === 'entrata' ? '+' : '-'}{formatCurrency(entry.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={entry.payment_status === 'pagato' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}
                        >
                          {entry.payment_status === 'pagato' ? 'Pagato' : 'Da pagare'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {entry.file_url ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${entry.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
                          >
                            <Download size={14} />
                          </a>
                        ) : isRole('admin', 'qhse') ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowUpload(entry.id)}
                            className="text-xs p-1 h-auto"
                          >
                            <Upload size={14} />
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                      {isRole('admin') && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 h-auto"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flusso Mensile */}
        <TabsContent value="flusso">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base" data-testid="flusso-title">Flusso di Cassa Mensile - {filterYear}</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mese</TableHead>
                    <TableHead className="text-right text-green-700">SAL Approvato</TableHead>
                    <TableHead className="text-right text-blue-700">SAL Fatturato</TableHead>
                    <TableHead className="text-right text-purple-700">SAL Incassato</TableHead>
                    <TableHead className="text-right text-emerald-700">Entrate Manuali</TableHead>
                    <TableHead className="text-right text-red-700">Uscite</TableHead>
                    <TableHead className="text-right font-bold">Saldo Mese</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!dashboard?.monthly_flow?.length ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessun flusso mensile disponibile</TableCell></TableRow>
                  ) : dashboard.monthly_flow.map((m, i) => {
                    const saldo = (m.sal_incassato || 0) + (m.entrate || 0) - (m.uscite || 0);
                    return (
                      <TableRow key={i} className="hover:bg-slate-50" data-testid={`flusso-row-${m.month}`}>
                        <TableCell className="font-medium text-sm">{m.month}</TableCell>
                        <TableCell className="text-right text-sm text-green-700">{formatCurrency(m.sal_approvato)}</TableCell>
                        <TableCell className="text-right text-sm text-blue-700">{formatCurrency(m.sal_fatturato)}</TableCell>
                        <TableCell className="text-right text-sm text-purple-700">{formatCurrency(m.sal_incassato)}</TableCell>
                        <TableCell className="text-right text-sm text-emerald-700">{formatCurrency(m.entrate)}</TableCell>
                        <TableCell className="text-right text-sm text-red-600">{formatCurrency(m.uscite)}</TableCell>
                        <TableCell className="text-right text-sm font-bold">
                          <span className={saldo >= 0 ? 'text-green-700' : 'text-red-700'}>{formatCurrency(saldo)}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals Row */}
                  {dashboard?.monthly_flow?.length > 0 && (() => {
                    const totFlow = dashboard.monthly_flow.reduce((acc, m) => ({
                      sal_approvato: acc.sal_approvato + (m.sal_approvato || 0),
                      sal_fatturato: acc.sal_fatturato + (m.sal_fatturato || 0),
                      sal_incassato: acc.sal_incassato + (m.sal_incassato || 0),
                      entrate: acc.entrate + (m.entrate || 0),
                      uscite: acc.uscite + (m.uscite || 0),
                    }), { sal_approvato: 0, sal_fatturato: 0, sal_incassato: 0, entrate: 0, uscite: 0 });
                    const saldoTot = totFlow.sal_incassato + totFlow.entrate - totFlow.uscite;
                    return (
                      <TableRow className="bg-slate-100 font-bold" data-testid="flusso-totals">
                        <TableCell>TOTALE</TableCell>
                        <TableCell className="text-right text-green-700">{formatCurrency(totFlow.sal_approvato)}</TableCell>
                        <TableCell className="text-right text-blue-700">{formatCurrency(totFlow.sal_fatturato)}</TableCell>
                        <TableCell className="text-right text-purple-700">{formatCurrency(totFlow.sal_incassato)}</TableCell>
                        <TableCell className="text-right text-emerald-700">{formatCurrency(totFlow.entrate)}</TableCell>
                        <TableCell className="text-right text-red-700">{formatCurrency(totFlow.uscite)}</TableCell>
                        <TableCell className="text-right">
                          <span className={saldoTot >= 0 ? 'text-green-700' : 'text-red-700'}>{formatCurrency(saldoTot)}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Visual bars for each month */}
          {dashboard?.monthly_flow?.length > 0 && (
            <Card className="border-slate-200 shadow-sm mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Andamento Visuale</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dashboard.monthly_flow.map((m, i) => {
                  const maxVal = Math.max(...dashboard.monthly_flow.map(x => Math.max(x.sal_incassato || 0, x.uscite || 0, x.entrate || 0)), 1);
                  const incW = Math.round(((m.sal_incassato || 0) / maxVal) * 100);
                  const entW = Math.round(((m.entrate || 0) / maxVal) * 100);
                  const uscW = Math.round(((m.uscite || 0) / maxVal) * 100);
                  return (
                    <div key={i} className="space-y-1">
                      <p className="text-xs font-medium text-slate-600">{m.month}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-purple-600 w-16 shrink-0">Incassato</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                          <div className="bg-purple-500 h-full rounded-full transition-all" style={{ width: `${incW}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-20 text-right">{formatCurrency(m.sal_incassato)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-600 w-16 shrink-0">Entrate</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                          <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${entW}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-20 text-right">{formatCurrency(m.entrate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600 w-16 shrink-0">Uscite</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                          <div className="bg-red-500 h-full rounded-full transition-all" style={{ width: `${uscW}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-20 text-right">{formatCurrency(m.uscite)}</span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Movement Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Movimento Contabile</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Cantiere *</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="entry-site-select">
                  <SelectValue placeholder="Seleziona cantiere..." />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrata">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={14} className="text-green-600" /> Entrata
                    </div>
                  </SelectItem>
                  <SelectItem value="uscita">
                    <div className="flex items-center gap-2">
                      <TrendingDown size={14} className="text-red-600" /> Uscita
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Importo (EUR) *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                data-testid="entry-amount"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>N. Fattura</Label>
              <Input
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                placeholder="es. FT-2026/001"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Data Fattura</Label>
              <Input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Controparte (Fornitore/Cliente)</Label>
              <Input
                value={form.counterpart}
                onChange={(e) => setForm({ ...form, counterpart: e.target.value })}
                placeholder="Nome fornitore o cliente"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Descrizione</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descrizione del movimento"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Stato Pagamento</Label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">Da Pagare</SelectItem>
                  <SelectItem value="pagato">Pagato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Metodo Pagamento</Label>
              <Select value={form.payment_method || '__none__'} onValueChange={(v) => setForm({ ...form, payment_method: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="--" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">--</SelectItem>
                  <SelectItem value="bonifico">Bonifico Bancario</SelectItem>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                  <SelectItem value="carta">Carta</SelectItem>
                  <SelectItem value="ri.ba">Ri.Ba</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.payment_method === 'bonifico' && (
              <>
                <div className="space-y-1.5"><Label>IBAN</Label><Input value={form.bank_iban} onChange={(e) => setForm({ ...form, bank_iban: e.target.value })} placeholder="IT..." /></div>
                <div className="space-y-1.5"><Label>CRO/TRN</Label><Input value={form.bank_cro} onChange={(e) => setForm({ ...form, bank_cro: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Data Valuta</Label><Input type="date" value={form.bank_valuta} onChange={(e) => setForm({ ...form, bank_valuta: e.target.value })} /></div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button 
              onClick={handleCreate} 
              disabled={!form.site_id || !form.amount || !form.date}
              data-testid="entry-save-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={!!showUpload} onOpenChange={() => setShowUpload(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Carica Fattura</DialogTitle>
          </DialogHeader>
          <Input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              if (e.target.files?.[0] && showUpload) {
                handleUpload(showUpload, e.target.files[0]);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
